import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { Avatar, Chip } from '@/ui/primitives';
import { formatName, formatRelative } from '@/lib/format';
import { formatCpf, STATUS_LABEL, STATUS_TONE } from '../data';
import type { ClienteListItem, StatusCliente } from '../schemas';

interface Props {
  clientes: ClienteListItem[];
  search: string;
  statusFilter: StatusCliente | '';
  ufFilter: string;
  municipioFilter?: string;
  onSelect: (cpf: string) => void;
}

function sumArrendamentoHa(cliente: ClienteListItem): number {
  return (cliente.arrendamentos ?? []).reduce((sum, arr) => {
    const status = String(arr.status ?? '').toLowerCase();
    if (status && status !== 'ativo' && status !== 'renovado') return sum;
    return sum + (arr.area_ha ?? 0);
  }, 0);
}

export function ClienteList({
  clientes,
  search,
  statusFilter,
  ufFilter,
  municipioFilter = '',
  onSelect,
}: Props) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'nome', desc: false }]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const muni = municipioFilter.trim().toLowerCase();
    return clientes.filter((c) => {
      if (statusFilter && c.status !== statusFilter) return false;
      if (ufFilter && (c.uf ?? '').toUpperCase() !== ufFilter.toUpperCase()) return false;
      if (muni && (c.municipio ?? '').trim().toLowerCase() !== muni) return false;
      if (!q) return true;
      return (
        c.nome.toLowerCase().includes(q) ||
        c.cpf.includes(q) ||
        (c.municipio ?? '').toLowerCase().includes(q)
      );
    });
  }, [clientes, search, statusFilter, ufFilter, municipioFilter]);

  const columns = useMemo<ColumnDef<ClienteListItem>[]>(
    () => [
      {
        id: 'nome',
        accessorKey: 'nome',
        header: 'Cliente',
        cell: ({ row }) => {
          const c = row.original;
          const nome = formatName(c.nome);
          return (
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name={nome} size="sm" />
              <span className="text-sm text-text truncate">{nome}</span>
            </div>
          );
        },
      },
      {
        id: 'cpf',
        accessorKey: 'cpf',
        header: 'CPF',
        cell: ({ row }) => (
          <span className="text-sm text-text-2 tabular-nums">{formatCpf(row.original.cpf)}</span>
        ),
      },
      {
        id: 'municipio',
        accessorKey: 'municipio',
        header: 'Município',
        cell: ({ row }) => (
          <span className="text-sm text-text-2">
            {row.original.municipio ?? '—'}
            {row.original.uf && ` / ${row.original.uf}`}
          </span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        cell: ({ getValue }) => {
          const s = (getValue() as string) as StatusCliente;
          const tone = STATUS_TONE[s] ?? 'neutral';
          const label = STATUS_LABEL[s] ?? s;
          return (
            <Chip tone={tone} size="sm" dot>
              {label}
            </Chip>
          );
        },
      },
      {
        id: 'imoveis',
        accessorFn: (row) => row.imoveis.length,
        header: 'Imóveis',
        cell: ({ row }) => (
          <span className="text-sm text-text tabular-nums">{row.original.imoveis.length}</span>
        ),
      },
      {
        id: 'hectares',
        accessorFn: (row) =>
          row.imoveis.reduce((sum, i) => sum + (i.area_total_ha ?? 0), 0)
          + sumArrendamentoHa(row),
        header: 'Hectares',
        cell: ({ getValue }) => {
          const ha = getValue() as number;
          if (!ha) return <span className="text-xs text-text-muted">—</span>;
          return (
            <Chip tone="info" size="sm">
              {ha.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} ha
            </Chip>
          );
        },
      },
      {
        id: 'score',
        accessorKey: 'score_regularidade',
        header: 'Score',
        cell: ({ getValue }) => {
          const s = getValue() as number | null | undefined;
          if (s == null) return <span className="text-xs text-text-muted">—</span>;
          const tone = s >= 80 ? 'ok' : s >= 50 ? 'warn' : 'danger';
          return (
            <Chip tone={tone} size="sm">
              {s.toFixed(0)}%
            </Chip>
          );
        },
      },
    ],
    [],
  );

  const table = useReactTable({
    data: filtered,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden">
      <table className="ds-table">
        <thead>
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((h) => (
                <th
                  key={h.id}
                  onClick={
                    h.column.getCanSort() ? h.column.getToggleSortingHandler() : undefined
                  }
                  className={h.column.getCanSort() ? 'cursor-pointer select-none' : ''}
                >
                  {flexRender(h.column.columnDef.header, h.getContext())}
                  {h.column.getIsSorted() === 'asc' && ' ↑'}
                  {h.column.getIsSorted() === 'desc' && ' ↓'}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              onClick={() => onSelect(row.original.cpf)}
              className="cursor-pointer"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="p-6 text-center text-sm text-text-muted">
          Nenhum cliente encontrado.
          <span className="block mt-1 text-[10px]">
            Última atualização: {formatRelative(new Date())}
          </span>
        </div>
      )}
    </div>
  );
}
